import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import '../CreateTablePage/styles.css';
import './styles.css';
import { showSuccess } from '../../utils/toast';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const BLANK_FIELD = {
  name: '',
  type: 'text',
  required: false,
  ref_schema_id: '',
  dedupe: false,
  search: false,
  seq: false,
  seqPrefix: '',
  seqStart: 1,
};

const EditTablePage = ({
  schemaId: propSchemaId = null,
  isInline = false,
  isOrgTemplate = false,
  onClose = null,
  onDeleted = null,
}) => {
  const navigate = useNavigate();
  const { schemaId: paramSchemaId } = useParams();
  const schemaId = propSchemaId || paramSchemaId;
  const [schema, setSchema] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [masterOptions, setMasterOptions] = useState([]);

  const isMasterSchema = schema?.is_master === true || schema?.schema_definition?.is_master === true;
  const isLockedOrgTemplate =
    isOrgTemplate === true || schema?.is_org_template === true;

  const dataTypes = [
    { value: 'text', label: 'Text' },
    { value: 'integer', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'enum', label: 'Dropdown' },
    { value: 'boolean', label: 'Yes / no' },
    { value: 'attachment', label: 'Attachment' },
    ...(!isMasterSchema ? [{ value: 'reference', label: 'Reference' }] : []),
  ];

  useEffect(() => {
    if (schemaId) {
      fetchSchema();
      fetchMasterOptions();
    }
  }, [schemaId]);

  const fetchMasterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/schemas`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const masters = (data.data || [])
        .filter((item) => item.is_master === true || item.schema_definition?.is_master === true)
        .map((item) => ({ id: item.schema_id, label: item.table_name }));
      setMasterOptions(masters);
    } catch (err) {
      // non-blocking
    }
  };

  const mapColumnsToFields = (columns = []) =>
    columns.map((column) => ({
      name: column.name || '',
      type: column.type || 'text',
      required: column.required === true,
      ref_schema_id: column.ref_schema_id || '',
      dedupe: column.dedupe === true,
      search: column.search === true,
      seq: column.seq === true,
      seqPrefix: column.seqPrefix || column.seq_prefix || '',
      seqStart: column.seqStart || column.seq_start || 1,
    }));

  const fetchSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/schemas/${schemaId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const data = await response.json();
      const schemaData = data.data;
      setSchema(schemaData);
      const columns = schemaData?.schema_definition?.columns || [];
      setFields(columns.length > 0 ? mapColumnsToFields(columns) : [{ ...BLANK_FIELD }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addField = () => {
    setFields((prev) => [...prev, { ...BLANK_FIELD }]);
  };

  const removeField = (index) => {
    setFields((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateField = (index, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      if (key === 'type') {
        next[index].ref_schema_id = value === 'reference' ? next[index].ref_schema_id || '' : '';
      }
      if (key === 'seq' && !value) {
        next[index].seqPrefix = '';
        next[index].seqStart = 1;
      }
      return next;
    });
  };

  const handleCancel = () => {
    if (isInline && onClose) {
      onClose();
      return;
    }
    navigate('/visuals/tables');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (isLockedOrgTemplate) {
      setError('Organization templates cannot be edited');
      return;
    }

    if (fields.length === 0) {
      setError('At least one field is required');
      return;
    }

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field.name.trim()) {
        setError(`Field ${i + 1} name is required`);
        return;
      }
      if (field.type === 'reference' && !field.ref_schema_id) {
        setError(`Field ${i + 1} must be linked to a master table`);
        return;
      }
    }

    const fieldNames = fields.map((field) => field.name.trim().toLowerCase());
    if (new Set(fieldNames).size !== fieldNames.length) {
      setError('Field names must be unique');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/schemas/${schemaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          columns: fields.map((field) => {
            const mapped = {
              name: field.name.trim(),
              type: field.type,
              required: field.required === true,
              dedupe: field.dedupe === true,
              search: field.search === true,
              seq: isMasterSchema && field.seq === true,
            };
            if (field.type === 'reference' && field.ref_schema_id) {
              mapped.ref_schema_id = field.ref_schema_id;
            }
            if (mapped.seq) {
              mapped.seqPrefix = field.seqPrefix || '';
              const start = parseInt(field.seqStart, 10);
              mapped.seqStart = Number.isFinite(start) && start > 0 ? start : 1;
            }
            return mapped;
          }),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update fields');
      }

      const msg = 'Fields updated successfully';
      setSuccess(msg);
      showSuccess(msg);

      if (isInline && onClose) {
        onClose();
        return;
      }

      await fetchSchema();
      setTimeout(() => {
        navigate('/visuals/tables');
      }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = () => {
    if (isLockedOrgTemplate) return;
    setConfirmText('');
    setConfirmOpen(true);
  };

  const closeDeleteModal = () => {
    if (!deleting) setConfirmOpen(false);
  };

  const handleDeleteTable = async () => {
    if (!schema?.schema_id) return;
    setError('');
    setSuccess('');
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const query = isOrgTemplate ? '?is_org_template=true' : '';
      const response = await fetch(`${API_URL}/schemas/${schema.schema_id}${query}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete table');
      }
      setSuccess('Table deleted successfully');
      showSuccess('Table deleted successfully');
      setConfirmOpen(false);
      setTimeout(() => {
        if (isInline && onDeleted) {
          onDeleted();
        } else if (isInline && onClose) {
          onClose();
        } else {
          navigate('/visuals/tables');
        }
      }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className={isInline ? 'builder-card' : 'edit-table-container'}>
        <div className="edit-table-loading">
          <div className="loading-spinner"></div>
          <p>Loading fields...</p>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={isInline ? 'builder-card' : 'edit-table-container'}>
        <div className="edit-table-error">
          <h3>Schema not found</h3>
          <p>The requested table schema could not be found.</p>
          <button onClick={handleCancel} className="builder-btn">
            {isInline ? 'Cancel' : 'Back to Tables'}
          </button>
        </div>
      </div>
    );
  }

  const pageTitle = schema.table_name || 'this page';

  if (isLockedOrgTemplate) {
    const lockedContent = (
      <div className="builder-card edit-fields-card">
        <h3 className="builder-title" style={{ marginBottom: 4 }}>
          Organization template
        </h3>
        <p className="builder-modal-sub">
          Field definitions for &ldquo;{pageTitle}&rdquo; are shared across all schools and cannot
          be edited here.
        </p>
        <div className="builder-footer-actions">
          <button type="button" onClick={handleCancel} className="builder-btn">
            {isInline ? 'Back' : 'Back to Tables'}
          </button>
        </div>
      </div>
    );

    if (isInline) return lockedContent;

    return (
      <SidebarLayout>
        <div className="edit-table-container">
          <div className="edit-table-header">
            <div>
              <h1>{schema.table_name}</h1>
              <p>Shared organization template</p>
            </div>
          </div>
          {lockedContent}
        </div>
      </SidebarLayout>
    );
  }

  const builderContent = (
    <div className="builder-card edit-fields-card">
      <form onSubmit={handleSubmit} className="builder-form">
        <h3 className="builder-title" style={{ marginBottom: 4 }}>
          Edit fields
        </h3>
        <p className="builder-modal-sub">
          Changing these updates the columns on &ldquo;{pageTitle}&rdquo; and its mapping into the
          analytics schema.
        </p>

        <div className={`field-block ${isMasterSchema ? '' : 'no-seq'}`}>
          <div className={`field-legend ${isMasterSchema ? 'field-legend-master' : ''}`}>
            <span>Field name</span>
            <span>Type</span>
            <span className="legend-check" title="Use for duplicate detection on import">
              Dupe
            </span>
            <span className="legend-check" title="Show a search filter for this field on the table">
              Search
            </span>
            <span className="legend-seq" title="Auto-generate as a sequence">
              Seq
            </span>
            <span />
          </div>
          <div className="builder-field-list">
            {fields.map((field, index) => (
              <div key={index} className="field-row-block">
                <div
                  className={`field-row ${field.type === 'reference' ? 'field-row-ref' : ''} ${
                    isMasterSchema ? 'field-row-master' : ''
                  }`}
                >
                  <input
                    type="text"
                    value={field.name}
                    onChange={(e) => updateField(index, 'name', e.target.value)}
                    placeholder="Field name"
                    className="builder-input"
                    required
                  />
                  <select
                    value={field.type}
                    onChange={(e) => updateField(index, 'type', e.target.value)}
                    className="builder-select"
                  >
                    {dataTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {field.type === 'reference' && (
                    <select
                      value={field.ref_schema_id || ''}
                      onChange={(e) => updateField(index, 'ref_schema_id', e.target.value)}
                      className="builder-select"
                      required
                    >
                      <option value="">Linked to...</option>
                      {masterOptions.length === 0 ? (
                        <option value="" disabled>
                          No master tables yet
                        </option>
                      ) : (
                        masterOptions.map((master) => (
                          <option key={master.id} value={master.id}>
                            {master.label}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                  <label className="dedupe-check" title="Use this field to detect duplicates">
                    <input
                      type="checkbox"
                      checked={field.dedupe === true}
                      onChange={(e) => updateField(index, 'dedupe', e.target.checked)}
                    />
                  </label>
                  <label
                    className="search-check"
                    title="Show a search filter for this field on the table"
                  >
                    <input
                      type="checkbox"
                      checked={field.search === true}
                      onChange={(e) => updateField(index, 'search', e.target.checked)}
                    />
                  </label>
                  {isMasterSchema && (
                    <label className="seq-check" title="Auto-generate as a sequence">
                      <input
                        type="checkbox"
                        checked={field.seq === true}
                        onChange={(e) => updateField(index, 'seq', e.target.checked)}
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => removeField(index)}
                    className="field-remove-btn"
                    aria-label="Remove field"
                    disabled={fields.length === 1}
                    title="Remove field"
                  >
                    ×
                  </button>
                </div>
                {isMasterSchema && field.seq && (
                  <div className="seq-config">
                    <input
                      type="text"
                      className="builder-input field-seq-prefix"
                      placeholder="Prefix e.g. ADM-"
                      value={field.seqPrefix || ''}
                      onChange={(e) => updateField(index, 'seqPrefix', e.target.value)}
                    />
                    <input
                      type="number"
                      className="builder-input field-seq-start"
                      min={1}
                      value={field.seqStart || 1}
                      onChange={(e) => updateField(index, 'seqStart', e.target.value)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={addField} className="add-field-btn">
            + Add field
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="builder-footer">
          <span className="builder-hint">
            Removing a field also removes its data from existing records
          </span>
          <div className="builder-footer-actions">
            <button type="button" onClick={handleCancel} className="builder-btn" disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || fields.length === 0}
              className="builder-btn builder-btn-accent"
            >
              {submitting ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>

      {confirmOpen && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete table &quot;{schema?.table_name}&quot;</h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                This action cannot be undone. This will permanently mark the table as deleted for
                your school.
              </p>
              <p className="modal-subtext">
                Please type{' '}
                <strong style={{ color: '#000', fontWeight: 'bold' }}>
                  Delete {schema?.table_name}
                </strong>{' '}
                to confirm.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={`Type Delete ${schema?.table_name} to confirm`}
                className={`confirm-input ${
                  confirmText === `Delete ${schema?.table_name}`
                    ? 'valid'
                    : confirmText
                      ? 'invalid'
                      : ''
                }`}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-btn"
                disabled={deleting || confirmText !== `Delete ${schema?.table_name}`}
                onClick={handleDeleteTable}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isInline) {
    return builderContent;
  }

  return (
    <SidebarLayout>
      <div className="edit-table-container">
        <div className="edit-table-header">
          <div>
            <h1>Edit fields: {schema.table_name}</h1>
            <p>Update columns for this table</p>
          </div>
          <div className="edit-table-actions">
            <button type="button" onClick={handleCancel} className="edit-table-back-btn">
              Back to Tables
            </button>
            {!isLockedOrgTemplate && (
              <button type="button" onClick={openDeleteModal} className="danger-outline-btn">
                Delete Table
              </button>
            )}
          </div>
        </div>
        {builderContent}
      </div>
    </SidebarLayout>
  );
};

export default EditTablePage;
